/* eslint-disable max-classes-per-file */
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import type { ConsentPurpose } from '@rover/rsdk/src/modules/Analytics';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import { AnalyticsEvent } from '@rover/types/src/Analytics';

// Maps geocode `locationType` to an input-method bucket; unknown types -> `other`.
export const LOCATION_METHOD_BY_TYPE: Record<string, string> = {
  'zip-code': 'zip_entry',
  address: 'address_entry',
  city: 'city_entry',
  neighborhood: 'neighborhood_entry',
  'current-location': 'current_location',
};

export const resolveLocationMethod = (locationType: string | null | undefined): string =>
  (locationType && LOCATION_METHOD_BY_TYPE[locationType]) || 'other';

abstract class SearchByNameEvent extends AnalyticsEvent {
  abstract eventName: string;

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.recommendations.browse_search;

  constructor(
    eventProperties: Record<string, unknown> = {},
    observabilityTags: Record<string, unknown> = {}
  ) {
    super(
      { ...eventProperties, source: isMobileBrowser() ? 'mobile_web' : 'web' },
      observabilityTags
    );
  }
}

export class SearchNameModalOpenedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-modal-opened';

  constructor(eventProperties: { openMethod: 'button' | 'deeplink'; isAuthenticated: boolean }) {
    super(eventProperties, {
      openMethod: eventProperties.openMethod,
      isAuthenticated: eventProperties.isAuthenticated,
    });
  }
}

export class SearchNameModalClosedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-modal-closed';

  constructor(eventProperties: {
    hasSearched: boolean;
    resultCount: number;
    isZeroResults: boolean;
    searchUuid: string | null;
    isAuthenticated: boolean;
    // Time results were on screen: from the first non-empty results render to close. 0 if none rendered.
    dwellTimeMs: number;
  }) {
    super(eventProperties, {
      hasSearched: eventProperties.hasSearched,
      resultCount: eventProperties.resultCount,
      isZeroResults: eventProperties.isZeroResults,
      searchUuid: eventProperties.searchUuid,
      isAuthenticated: eventProperties.isAuthenticated,
      dwellTimeMs: eventProperties.dwellTimeMs,
    });
  }
}

export class SearchNameQuerySubmittedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-query-submitted';

  constructor(eventProperties: {
    searchQuery: string;
    location: string;
    locationZip: string | null;
    searchQueryWordCount: number;
    searchQueryLength: number;
    queryNumberInSession: number;
  }) {
    super(eventProperties, {
      searchQueryWordCount: eventProperties.searchQueryWordCount,
      searchQueryLength: eventProperties.searchQueryLength,
      queryNumberInSession: eventProperties.queryNumberInSession,
    });
  }
}

export class SearchNameResultsRenderedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-results-rendered';

  constructor(eventProperties: {
    resultCount: number;
    isZeroResults: boolean;
    searchUuid: string | null;
    latencyMs: number;
    // OPKs of every sitter in the rendered result set, in display order.
    sitterPersonOpks: string[];
  }) {
    super(eventProperties, {
      resultCount: eventProperties.resultCount,
      isZeroResults: eventProperties.isZeroResults,
      searchUuid: eventProperties.searchUuid,
      latencyMs: eventProperties.latencyMs,
    });
  }
}

export type SearchNameCardClickTarget = 'profile' | 'intro-message';

export class SearchNameCardClickedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-card-clicked';

  constructor(eventProperties: {
    rank: number;
    sitterPersonOpk: string;
    resultCount: number;
    // Typed nullable to match the other events' searchUuid; always non-null at the emit site.
    searchUuid: string | null;
    isPreviouslyBooked: boolean;
    isStarSitter: boolean;
    clickTarget: SearchNameCardClickTarget;
  }) {
    super(eventProperties, {
      rank: eventProperties.rank,
      sitterPersonOpk: eventProperties.sitterPersonOpk,
      resultCount: eventProperties.resultCount,
      searchUuid: eventProperties.searchUuid,
      isPreviouslyBooked: eventProperties.isPreviouslyBooked,
      isStarSitter: eventProperties.isStarSitter,
      clickTarget: eventProperties.clickTarget,
    });
  }
}

export class SearchNameLocationChangedEvent extends SearchByNameEvent {
  eventName = 'search-by-name-location-changed';

  constructor(eventProperties: {
    locationMethod: string;
    coordsResolved: boolean;
    hadResultsBefore: boolean;
  }) {
    super(eventProperties, {
      locationMethod: eventProperties.locationMethod,
      coordsResolved: eventProperties.coordsResolved,
      hadResultsBefore: eventProperties.hadResultsBefore,
    });
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires once per result card when it first scrolls ≥75% into view; `rank` is the
 * card's 1-based position.
 *
 * &nbsp;
 */
export class SearchNameScrollDepthEvent extends SearchByNameEvent {
  eventName = 'search-by-name-scroll-depth';

  constructor(eventProperties: {
    rank: number;
    sitterPersonOpk: string;
    resultCount: number;
    searchUuid: string | null;
    isPreviouslyBooked: boolean;
    isStarSitter: boolean;
  }) {
    super(eventProperties, {
      rank: eventProperties.rank,
      sitterPersonOpk: eventProperties.sitterPersonOpk,
      isPreviouslyBooked: eventProperties.isPreviouslyBooked,
      isStarSitter: eventProperties.isStarSitter,
    });
  }
}
